import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaBroadcastTower, FaSearch, FaDownload, FaPlay, FaStop, FaEraser, FaSync } from 'react-icons/fa';
import { FiCpu } from 'react-icons/fi';
import PropTypes from 'prop-types';
import apiAxios from 'helpers/apiAxios';
import { toast } from 'react-toastify';

const BASE = '/monitoring';

// Level detection + colors
const LEVELS = {
  error: { color: 'tw-text-rose-600',    dot: 'tw-bg-rose-500' },
  warn:  { color: 'tw-text-amber-600',   dot: 'tw-bg-amber-400' },
  info:  { color: 'tw-text-sky-600',     dot: 'tw-bg-sky-400' },
  debug: { color: 'tw-text-slate-400',   dot: 'tw-bg-slate-300' },
};
const detectLevel = (msg) => {
  const s = (msg || '').toLowerCase();
  if (/\berror\b|exception|fatal|stacktrace/.test(s)) return 'error';
  if (/\bwarn(ing)?\b/.test(s)) return 'warn';
  if (/\bdebug\b/.test(s)) return 'debug';
  return 'info';
};

// SSE via token in URL — because EventSource doesn't support custom headers.
// The FE JWT is stored somewhere accessible; here we grab via cookie if any,
// otherwise let the request 401 (user needs to be logged in).
const getJwtCookie = () => {
  const m = document.cookie.match(/(?:^|; )token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
};

const MonitoringLogs = () => {
  const [tab, setTab] = useState('live');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Live tail
  const [liveGroup, setLiveGroup] = useState('');
  const [liveFilter, setLiveFilter] = useState('');
  const [pollMs, setPollMs] = useState(1500);
  const [tailing, setTailing] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [status, setStatus] = useState('idle');
  const [events, setEvents] = useState([]);
  const esRef = useRef(null);
  const outRef = useRef(null);

  // Search
  const [searchGroup, setSearchGroup] = useState('');
  const [searchQuery, setSearchQuery] = useState('fields @timestamp, @message | sort @timestamp desc');
  const [searchRange, setSearchRange] = useState(60);
  const [searchLimit, setSearchLimit] = useState(200);
  const [searchStatus, setSearchStatus] = useState('idle');
  const [searchResults, setSearchResults] = useState([]);

  // System
  const [sysInfo, setSysInfo] = useState(null);
  const [sysLoading, setSysLoading] = useState(false);

  // ── Load groups ────────────────────────────────────────────────────────────
  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const res = await apiAxios.get(`${BASE}/logs/groups`, { params: { limit: 50 } });
      const gs = res.data.groups || [];
      setGroups(gs);
      if (gs.length && !liveGroup) setLiveGroup(gs[0].name);
      if (gs.length && !searchGroup) setSearchGroup(gs[0].name);
    } catch (e) {
      toast.error('Load groups failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoadingGroups(false);
    }
  }, [liveGroup, searchGroup]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => () => { if (esRef.current) esRef.current.close(); }, []);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (autoScroll && outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [events, autoScroll]);

  // ── Live tail ──────────────────────────────────────────────────────────────
  const startTail = () => {
    if (!liveGroup) return toast.error('Chọn log group trước');
    stopTail();

    const qs = new URLSearchParams({ group: liveGroup, poll_ms: String(pollMs) });
    if (liveFilter) qs.set('filter', liveFilter);
    const token = getJwtCookie();
    if (token) qs.set('token', token);

    // Note: apiAxios has baseURL, but we need window-relative URL for EventSource.
    // Use full path with the same base:
    const baseUrl = apiAxios.defaults.baseURL || '';
    const url = `${baseUrl}${BASE}/logs/tail?${qs.toString()}`;

    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    setTailing(true);
    setStatus('connecting…');

    es.addEventListener('hello', () => setStatus('streaming'));
    es.addEventListener('log', (e) => {
      try {
        const ev = JSON.parse(e.data);
        setEvents(prev => {
          const next = [...prev, ev];
          return next.length > 5000 ? next.slice(-4000) : next;
        });
      } catch { /* skip a malformed log line */ }
    });
    es.addEventListener('ping', () => setStatus('alive · ' + new Date().toLocaleTimeString()));
    es.addEventListener('error', () => setStatus('error'));
    es.onerror = () => setStatus('disconnected');
  };

  const stopTail = () => {
    if (esRef.current) { try { esRef.current.close(); } catch { /* already closed */ } esRef.current = null; }
    setTailing(false);
    setStatus('stopped');
  };

  const clearEvents = () => setEvents([]);

  // ── Search ─────────────────────────────────────────────────────────────────
  const runSearch = async () => {
    if (!searchGroup) return toast.error('Chọn log group trước');
    const endMs = Date.now();
    const startMs = endMs - searchRange * 60 * 1000;
    setSearchStatus('searching…');
    setSearchResults([]);
    try {
      const res = await apiAxios.get(`${BASE}/logs/search`, {
        params: { group: searchGroup, query: searchQuery, start_ms: startMs, end_ms: endMs, limit: searchLimit },
      });
      setSearchResults(res.data.results || []);
      setSearchStatus(`${res.data.results?.length || 0} rows (${res.data.status})`);
    } catch (e) {
      setSearchStatus('error');
      toast.error(e.response?.data?.error || e.message);
    }
  };

  // ── System ─────────────────────────────────────────────────────────────────
  const loadSystem = async () => {
    setSysLoading(true);
    try {
      const res = await apiAxios.get(`${BASE}/system/info`);
      setSysInfo(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setSysLoading(false); }
  };
  // Lazy-load once when the System tab is opened (Refresh button reloads); re-running on
  // sysInfo changes could refetch again when the endpoint returns an empty body.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'system' && !sysInfo) loadSystem(); }, [tab]);

  // ── Download ───────────────────────────────────────────────────────────────
  const doDownload = (format) => {
    if (!liveGroup) return toast.error('Chọn log group trước');
    const endMs = Date.now();
    const startMs = endMs - 60 * 60 * 1000; // 1h
    const qs = new URLSearchParams({ group: liveGroup, start_ms: startMs, end_ms: endMs, format });
    if (liveFilter) qs.set('filter', liveFilter);
    const token = getJwtCookie();
    if (token) qs.set('token', token);
    const baseUrl = apiAxios.defaults.baseURL || '';
    window.open(`${baseUrl}${BASE}/logs/download?${qs.toString()}`, '_blank');
  };

  const searchCols = searchResults.length ? Object.keys(searchResults[0]).filter(k => k !== '@ptr') : [];

  return (
    <div className="tw-p-5 tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-items-center tw-justify-between">
        <div>
          <h2 className="tw-text-base tw-font-bold tw-text-slate-800 tw-flex tw-items-center tw-gap-2">
            <FaBroadcastTower size={13} className="tw-text-emerald-500" /> Production Logs (CloudWatch)
          </h2>
          <p className="tw-text-xs tw-text-slate-500 tw-mt-0.5">Live tail, search, và system info từ ECS task đang chạy.</p>
        </div>
        <button type="button" onClick={loadGroups} disabled={loadingGroups}
          className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-slate-600 tw-border tw-border-slate-200 tw-rounded hover:tw-bg-slate-50 tw-bg-white tw-cursor-pointer tw-border-solid">
          <FaSync size={11} className={loadingGroups ? 'tw-animate-spin' : ''} /> Refresh groups
        </button>
      </div>

      {/* Tabs */}
      <div className="tw-flex tw-gap-1 tw-border-b tw-border-slate-200">
        {[
          { k: 'live',   label: 'Live tail',    icon: <FaBroadcastTower size={11} /> },
          { k: 'search', label: 'Search',       icon: <FaSearch size={11} /> },
          { k: 'system', label: 'System info',  icon: <FiCpu size={12} /> },
        ].map(t => (
          <button key={t.k} type="button" onClick={() => setTab(t.k)}
            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-border-b-2 tw-cursor-pointer tw-bg-transparent tw-border-0 ${tab === t.k ? 'tw-text-primary-600' : 'tw-text-slate-500 hover:tw-text-slate-700'}`}
            style={tab === t.k ? { borderBottomColor: '#4f46e5', borderBottomStyle: 'solid', borderBottomWidth: 2 } : {}}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── LIVE TAIL ── */}
      {tab === 'live' && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center tw-p-3 tw-rounded tw-bg-slate-50 tw-border tw-border-slate-200">
            <select value={liveGroup} onChange={e => setLiveGroup(e.target.value)}
              className="tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-min-w-[280px]">
              <option value="">— Chọn Log Group —</option>
              {groups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
            </select>
            <input value={liveFilter} onChange={e => setLiveFilter(e.target.value)}
              placeholder='filter: "ERROR" hoặc { $.level = "error" }'
              className="tw-flex-1 tw-min-w-[200px] tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white" />
            <input type="number" value={pollMs} onChange={e => setPollMs(e.target.value)} min={500} step={500}
              className="tw-w-24 tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white" title="Poll ms" />
            {!tailing ? (
              <button type="button" onClick={startTail}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white tw-bg-emerald-600 tw-rounded-sm hover:tw-bg-emerald-700 tw-cursor-pointer tw-border-0">
                <FaPlay size={9} /> Start
              </button>
            ) : (
              <button type="button" onClick={stopTail}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white tw-bg-rose-600 tw-rounded-sm hover:tw-bg-rose-700 tw-cursor-pointer tw-border-0">
                <FaStop size={9} /> Stop
              </button>
            )}
            <button type="button" onClick={clearEvents}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-text-slate-600 tw-border tw-border-slate-300 tw-bg-white tw-rounded-sm hover:tw-bg-slate-100 tw-cursor-pointer tw-border-solid">
              <FaEraser size={9} /> Clear
            </button>
            <label className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-slate-600">
              <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} /> Auto-scroll
            </label>
            <button type="button" onClick={() => doDownload('json')}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1.5 tw-text-[11px] tw-text-slate-600 tw-border tw-border-slate-300 tw-bg-white tw-rounded-sm hover:tw-bg-slate-100 tw-cursor-pointer tw-border-solid ml-auto">
              <FaDownload size={9} /> JSON
            </button>
            <button type="button" onClick={() => doDownload('txt')}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1.5 tw-text-[11px] tw-text-slate-600 tw-border tw-border-slate-300 tw-bg-white tw-rounded-sm hover:tw-bg-slate-100 tw-cursor-pointer tw-border-solid">
              <FaDownload size={9} /> TXT
            </button>
            <span className="tw-text-[10px] tw-text-slate-500 tw-ml-2 tw-font-mono">{status}</span>
          </div>

          <div ref={outRef}
            className="tw-flex-1 tw-overflow-auto tw-rounded tw-border tw-border-slate-800 tw-bg-slate-900 tw-p-3 tw-font-mono tw-text-[11px] tw-text-slate-200"
            style={{ height: 460, minHeight: 300 }}>
            {events.length === 0 ? (
              <div className="tw-text-slate-500 tw-text-center tw-py-8">
                {tailing ? 'Đang chờ log mới…' : 'Bấm Start để bắt đầu tail.'}
              </div>
            ) : events.map((ev, i) => {
              const lv = detectLevel(ev.message);
              const c  = LEVELS[lv];
              return (
                <div key={i} className="tw-py-0.5 tw-leading-relaxed tw-break-all">
                  <span className={`tw-inline-block tw-w-1.5 tw-h-1.5 tw-rounded-xl ${c.dot} tw-mr-2`}></span>
                  <span className="tw-text-slate-500">{ev.ts}</span>{' '}
                  <span className="tw-text-purple-400">[{ev.stream}]</span>{' '}
                  <span className={c.color}>{ev.message}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SEARCH ── */}
      {tab === 'search' && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center tw-p-3 tw-rounded tw-bg-slate-50 tw-border tw-border-slate-200">
            <select value={searchGroup} onChange={e => setSearchGroup(e.target.value)}
              className="tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-min-w-[240px]">
              <option value="">— Chọn Log Group —</option>
              {groups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
            </select>
            <select value={searchRange} onChange={e => setSearchRange(parseInt(e.target.value, 10))}
              className="tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white">
              <option value={15}>Last 15m</option>
              <option value={60}>Last 1h</option>
              <option value={180}>Last 3h</option>
              <option value={720}>Last 12h</option>
              <option value={1440}>Last 24h</option>
            </select>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="CloudWatch Insights query"
              className="tw-flex-1 tw-min-w-[260px] tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-font-mono" />
            <input type="number" value={searchLimit} onChange={e => setSearchLimit(parseInt(e.target.value, 10))} min={10} step={50}
              className="tw-w-24 tw-px-3 tw-py-1.5 tw-text-xs tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white" title="Limit" />
            <button type="button" onClick={runSearch}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white tw-bg-primary-600 tw-rounded-sm hover:tw-bg-primary-700 tw-cursor-pointer tw-border-0">
              <FaSearch size={9} /> Run
            </button>
            <span className="tw-text-[10px] tw-text-slate-500 tw-ml-2 tw-font-mono">{searchStatus}</span>
          </div>

          <div className="tw-rounded tw-border tw-border-slate-200 tw-overflow-hidden tw-bg-white">
            <div className="tw-overflow-auto" style={{ maxHeight: 500 }}>
              <table className="tw-w-full tw-text-xs">
                {searchResults.length > 0 && (
                  <thead className="tw-sticky tw-top-0 tw-bg-slate-50 tw-border-b tw-border-slate-200">
                    <tr>{searchCols.map(c => <th key={c} className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-whitespace-nowrap">{c}</th>)}</tr>
                  </thead>
                )}
                <tbody className="tw-divide-y tw-divide-slate-100">
                  {searchResults.length === 0 ? (
                    <tr><td className="tw-py-8 tw-text-center tw-text-slate-400">Chưa có kết quả. Cấu hình query rồi bấm Run.</td></tr>
                  ) : searchResults.map((row, i) => (
                    <tr key={i} className="hover:tw-bg-slate-50">
                      {searchCols.map(c => <td key={c} className="tw-px-3 tw-py-2 tw-text-slate-800 tw-align-top tw-whitespace-pre-wrap tw-break-all tw-font-mono">{String(row[c] ?? '')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── SYSTEM ── */}
      {tab === 'system' && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-justify-end">
            <button type="button" onClick={loadSystem} disabled={sysLoading}
              className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-slate-600 tw-border tw-border-slate-200 tw-rounded-sm hover:tw-bg-slate-50 tw-bg-white tw-cursor-pointer tw-border-solid">
              <FaSync size={11} className={sysLoading ? 'tw-animate-spin' : ''} /> Reload
            </button>
          </div>
          {!sysInfo ? (
            <div className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-400">Loading…</div>
          ) : (
            <>
              <div className="tw-grid tw-gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
                <SysCard title="App"           obj={sysInfo.app} />
                <SysCard title="Process"       obj={sysInfo.process} />
                <SysCard title="ECS Env"       obj={sysInfo.ecs?.environment} />
                <SysCard title="ECS Container" obj={sysInfo.ecs?.container} />
                <SysCard title="ECS Task"      obj={sysInfo.ecs?.task} />
                <SysCard title="ECS Stats"     obj={sysInfo.ecs?.stats} />
              </div>
              <details className="tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50">
                <summary className="tw-px-3 tw-py-2 tw-cursor-pointer tw-text-xs tw-font-semibold tw-text-slate-700">Raw JSON</summary>
                <pre className="tw-p-3 tw-text-[11px] tw-text-slate-700 tw-overflow-auto tw-max-h-96">{JSON.stringify(sysInfo, null, 2)}</pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SysCard = ({ title, obj }) => (
  <div className="tw-p-3 tw-rounded tw-border tw-border-slate-200 tw-bg-white">
    <div className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-500 tw-mb-2">{title}</div>
    {!obj ? <div className="tw-text-xs tw-text-slate-400 tw-italic">— no data —</div> : Object.entries(obj).map(([k, v]) => (
      <div key={k} className="tw-flex tw-justify-between tw-gap-2 tw-py-0.5 tw-text-xs">
        <span className="tw-text-slate-500">{k}</span>
        <span className="tw-text-slate-800 tw-font-mono tw-truncate tw-max-w-[60%]" title={typeof v === 'object' ? JSON.stringify(v) : String(v)}>{typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}</span>
      </div>
    ))}
  </div>
);
SysCard.propTypes = {
  title: PropTypes.node,
  obj: PropTypes.object,
};

export default MonitoringLogs;
